<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: false,
    },
    parameters: {
      type: Object,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    autofocus: {
      type: Boolean,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_dataSource = computed(() => ({
    name: 'suggest_rental',
    parameters: props.parameters,
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_dataSource: b_7315092382398562_dataSource.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_dataSource', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_7315092382398562_dataSource },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.path,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('path');
    }
  );
  watch(
    () => props.name,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('name');
    }
  );
  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.autofocus,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('autofocus');
    }
  );
  watch(
    () => props.clearable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clearable');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpSelect2
    :name="name || path"
    v-model="item[path]"
    :value="item[path]"
    @input="
      !($event && typeof $event.stopPropagation === 'function') &&
      (item[path] =
        $event && typeof $event === 'object' && 'data' in $event
          ? $event.data
          : $event)
    "
    :dataSource="b_7315092382398562_dataSource"
    prefetch
    valueField="id"
    textField="name"
    bind-object
    :clearable="clearable"
    :autofocus="autofocus"
    :disabled="readonly || item.isReadonly"
    :readonly="readonly || item.isReadonly"
    :required="required"
    optionTemplate="<UcSuggestRentalOption :option=option></UcSuggestRentalOption>"
    use-option-template-for-selected-option
    v-if="item"
  >
  </ItpSelect2>
</template>
